import { Locale } from '@/constants/locales';
import { Url } from '@/constants/types';
import { Market } from '@avp/context-claims';

const isoLocaleToI18nLocale = (locale: Locale | undefined): string =>
  locale ? (locale as string).replace('_', '-') : 'none';
export class Localization {
  audiWebsites: Map<string, Url> = new Map()
    .set(Market.CAN, 'audi.ca')
    .set(Market.DEU, 'audi.de')
    .set(Market.FRA, 'audi.fr')
    .set(Market.ITA, 'audi.it')
    .set(Market.POL, 'audi.pl')
    .set(Market.CHE, 'audi.ch')
    .set(Market.ESP, 'audi.es')
    .set(Market.GBR, 'audi.co.uk')
    .set(Market.AUS, 'audi.com.au')
    .set(Market.BRA, 'audi.com.br')
    .set(Market.IRL, 'audi.ie')
    .set(Market.JPN, 'audi.co.jp')
    .set(Market.KOR, 'audi.co.kr')
    .set(Market.MEX, 'audi.com.mx')
    .set(Market.SWE, 'audi.se')
    .set(Market.DNK, 'audi.dk')
    .set(Market.USA, 'audiusa.com')
    .set(Market.SGP, 'audi.com.sg')
    .set(Market.ARE, 'audi-me.com')
    .set(Market.RUS, 'audi.ru')
    .set(Market.ZAF, 'audi.co.za')
    .set(Market.AUT, 'audi.at')
    .set(Market.TUR, 'audi.com.tr')
    .set(Market.LUX, 'audi.lu')
    .set(Market.BEL, 'audi.be')
    .set(Market.NLD, 'audi.nl')
    .set(Market.ARG, 'audi.com.ar')
    .set(Market.SAU, 'audi-me.com')
    .set(Market.QAT, 'audi-me.com')
    .set(Market.KWT, 'audi-me.com')
    .set(Market.OMN, 'audi-me.com')
    .set(Market.BHR, 'audi-me.com')
    .set(Market.LBN, 'audi-me.com')
    .set(Market.JOR, 'audi-me.com')
    .set(Market.EGY, 'audi-eg.com')
    .set(Market.FIN, 'audi.fi')
    .set(Market.GRC, 'audi.gr')
    .set(Market.NOR, 'audi.no')
    .set(Market.BOL, 'audi.bo')
    .set(Market.CHL, 'audi.cl')
    .set(Market.CYM, 'audi-caymanislands.com')
    .set(Market.CRI, 'audi.co.cr')
    .set(Market.CUW, 'audicuracao.com')
    .set(Market.DOM, 'audi.com.do')
    .set(Market.ECU, 'audi.com.ec')
    .set(Market.SLV, 'audi.com.sv')
    .set(Market.GUF, 'audi.gf')
    .set(Market.GLP, 'audi.gp')
    .set(Market.GTM, 'audi.com.gt')
    .set(Market.JAM, 'audijamaica.com')
    .set(Market.MTQ, 'audi-martinique.fr')
    .set(Market.PAN, 'audi.com.pa')
    .set(Market.PRY, 'audi.com.py')
    .set(Market.PER, 'audi.com.pe')
    .set(Market.SXM, 'audi-sxm.com')
    .set(Market.LCA, 'audi.lc')
    .set(Market.TTO, 'audi.tt')
    .set(Market.URY, 'audi.com.uy')
    .set(Market.COL, 'audi.com.co');

  cupraWebsites: Map<string, Url> = new Map()
    .set(Market.DEU, 'cupraofficial.de')
    .set(Market.ESP, 'cupraofficial.es')
    .set(Market.GBR, 'cupraofficial.co.uk');

  vwWebsites: Map<string, Url> = new Map()
    .set(Market.DEU, 'volkswagen.de')
    .set(Market.ESP, 'volkswagen.es')
    .set(Market.GBR, 'volkswagen.co.uk')
    .set(Market.FRA, 'volkswagen.fr')
    .set(Market.IRL, 'volkswagen.ie');

  vwConfiguratorSites: Map<string, Url> = new Map()
    .set(Market.DEU, 'https://www.volkswagen.de/de/konfigurator.html')
    .set(Market.ESP, 'https://www.volkswagen.es/es/configurador.html')
    .set(Market.GBR, 'https://www.volkswagen.co.uk/en/configurator.html')
    .set(Market.FRA, 'https://www.volkswagen.fr/fr/configurateur.html')
    .set(Market.IRL, 'https://www.volkswagen.ie/en/build.html');

  vwnConfiguratorSites: Map<string, Url> = new Map()
    .set(Market.DEU, 'https://www.volkswagen-nutzfahrzeuge.de/de/konfigurator.html')
    .set(Market.ESP, 'https://www.volkswagen-comerciales.es/es/configurador.html')
    .set(Market.GBR, 'https://www.volkswagen-vans.co.uk/en/build.html')
    .set(Market.FRA, 'https://www.volkswagen-utilitaires.fr/fr/configurer-votre-vehicule.html')
    .set(Market.IRL, 'https://www.volkswagenvans.ie/en/build.html');

  cupraConfiguratorSites: Map<string, Url> = new Map()
    .set(Market.DEU, 'https://www.cupraofficial.de/konfigurator.html/summary?wf=cupra-de')
    .set(Market.ESP, 'https://www.cupraofficial.es/configurador.html/summary?wf=cupra-es');

  // eslint-disable-next-line no-useless-constructor
  constructor(readonly defaultMarket = Market.DEU) {}

  getAudiWebsite(marketString?: string) {
    return this.audiWebsites.get(marketString || this.defaultMarket) || this.audiWebsites.get(this.defaultMarket);
  }

  getCupraWebsite(marketString?: string) {
    return this.cupraWebsites.get(marketString || this.defaultMarket) || this.cupraWebsites.get(this.defaultMarket);
  }

  getVWWebsite(marketString?: string) {
    return this.vwWebsites.get(marketString || this.defaultMarket) || this.vwWebsites.get(this.defaultMarket);
  }

  getVWConfiguratorWebsite(marketingCode: string, marketString?: string) {
    if (!marketingCode) {
      return '';
    }

    let configuratorSite;
    if (marketingCode.startsWith('N')) {
      configuratorSite =
        this.vwnConfiguratorSites.get(marketString || this.defaultMarket) ||
        this.vwnConfiguratorSites.get(this.defaultMarket);
    } else {
      configuratorSite =
        this.vwConfiguratorSites.get(marketString || this.defaultMarket) ||
        this.vwConfiguratorSites.get(this.defaultMarket);
    }

    return `${configuratorSite}/__app/route:configure.app?marketingCode-app=${marketingCode}`;
  }

  getCupraConfiguratorWebsite(configurationId: string, marketString?: string) {
    const configuratorSite =
      this.cupraConfiguratorSites.get(marketString || this.defaultMarket) ||
      this.cupraConfiguratorSites.get(this.defaultMarket);

    return `${configuratorSite}&id=${configurationId}`;
  }

  static getI18nLocale(locale?: Locale, market?: Market): string {
    return isoLocaleToI18nLocale(this.getLocale(locale, market));
  }

  static getLocale(locale?: Locale, market?: Market): Locale | undefined {
    if (locale) {
      return Object.keys(Locale).includes(locale) ? locale : undefined;
    } else if (market && market === Market.DEU) {
      return Locale.de_DE;
    }
    return Locale.en;
  }

  static isI18nLocaleValid(i18nLocale: string): boolean {
    return Object.keys(Locale).includes(i18nLocale.replace('-', '_'));
  }
}
